import { useState } from 'react';
import { useApi } from '../api/ApiProvider';
import type { DocumentSummary } from '../api/types';
import { Button } from './ui/Button';
import { Dialog } from './ui/Dialog';
import { ErrorBanner } from './ui/ErrorBanner';

type Pending = 'remove' | 'hold' | null;

/**
 * Hide / unhide / remove / legal hold for one document, rendered as a row of small buttons.
 * Only actions in `doc.actions` appear; the API enforces them again.
 */
export function RecordsActions({
  doc,
  onChanged,
  compact = false,
}: {
  doc: DocumentSummary;
  onChanged?: () => void;
  compact?: boolean;
}) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<Pending>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<Error | null>(null);

  const can = (a: string) => doc.actions.includes(a as DocumentSummary['actions'][number]);
  const showHide = !doc.hidden && can('hide');
  const showUnhide = doc.hidden && can('unhide');
  const showRemove = can('remove') && doc.status !== 'purged';
  const showHold = can('hold');
  if (!showHide && !showUnhide && !showRemove && !showHold) return null;

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setDialog(null);
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  };

  const size = compact ? 'sm' : 'md';

  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {showHide ? (
          <Button variant="secondary" size={size} icon="eye-off" busy={busy} onClick={() => void run(() => api.documents.hide(doc.id))}>
            Hide
          </Button>
        ) : null}
        {showUnhide ? (
          <Button variant="secondary" size={size} icon="eye" busy={busy} onClick={() => void run(() => api.documents.unhide(doc.id))}>
            Unhide
          </Button>
        ) : null}
        {showHold ? (
          <Button
            variant="secondary"
            size={size}
            icon="shield"
            busy={busy}
            onClick={() => {
              setReason('');
              setDialog('hold');
            }}
          >
            {doc.legalHold ? 'Release hold' : 'Legal hold'}
          </Button>
        ) : null}
        {showRemove ? (
          <Button
            variant="secondary"
            size={size}
            icon="x"
            busy={busy}
            onClick={() => {
              setReason('');
              setDialog('remove');
            }}
          >
            Remove
          </Button>
        ) : null}
      </div>
      {error ? (
        <div className="mt-2">
          <ErrorBanner error={error} />
        </div>
      ) : null}

      <Dialog
        open={dialog !== null}
        title={dialog === 'remove' ? 'Remove document' : doc.legalHold ? 'Release legal hold' : 'Apply legal hold'}
        onClose={() => setDialog(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button
              variant={dialog === 'remove' ? 'danger' : 'primary'}
              disabled={reason.trim().length < 5}
              busy={busy}
              onClick={() =>
                void run(() =>
                  dialog === 'remove'
                    ? api.documents.remove(doc.id, reason.trim())
                    : api.documents.hold(doc.id, !doc.legalHold, reason.trim()),
                )
              }
            >
              {dialog === 'remove' ? 'Remove' : doc.legalHold ? 'Release hold' : 'Apply hold'}
            </Button>
          </>
        }
      >
        <p>
          {dialog === 'remove' ? (
            <>
              This moves <strong>{doc.title}</strong> out of the folder and into quarantine. It is only possible inside the
              7-day grace window and is written to the audit trail with your reason. Nothing is deleted.
            </>
          ) : doc.legalHold ? (
            'Releasing the hold lets retention and hide/remove apply again.'
          ) : (
            'A legal hold blocks every removal path, including retention purge, until it is released.'
          )}
        </p>
        <label className="mt-3 block">
          <span className="text-xs font-medium text-muted-foreground">Reason (required)</span>
          <textarea
            className="gsl-input mt-1"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={dialog === 'remove' ? 'e.g. uploaded to the wrong customer' : undefined}
          />
        </label>
      </Dialog>
    </div>
  );
}
